import { message } from "antd";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Alert } from "@mantine/core";
import _ from "lodash";

import {
  getServiceUploadTemplate,
  uploadFacilityServices,
} from "../../../helpers/api";
import { downloadBlobResponse } from "../../../helpers/utilities";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // matches the api's multer limit

/** renders a list of per-row problems returned by the api */
const RowIssues = ({ title, rows, tone }) => {
  if (!rows?.length) return null;

  return (
    <div className="mt-3">
      <p className={`mb-1 fw-bold text-${tone}`}>
        {title} ({rows.length})
      </p>
      <div
        className="border rounded p-2"
        style={{ maxHeight: "180px", overflowY: "auto" }}>
        {rows.map((r, i) => (
          <p className="mb-1 small" key={`${r.row}-${i}`}>
            <span className="fw-bold">Row {r.row}</span>
            {r.name ? ` (${r.name})` : ""} — {r.message}
          </p>
        ))}
      </div>
    </div>
  );
};

export default function UploadServicesForm({ onSuccess }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    const res = await getServiceUploadTemplate();
    setIsDownloading(false);

    const { success, error } = await downloadBlobResponse(
      res,
      "rivia-services-upload-template.xlsx"
    );

    if (!success) message.error(error || "could not download the template");
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    setResult(null);

    if (!selected) {
      setFile(null);
      return;
    }

    const ext = selected.name.slice(selected.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      message.error("only excel files (.xlsx, .xls) are allowed");
      e.target.value = "";
      setFile(null);
      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      message.error("file is too large — the maximum size is 10MB");
      e.target.value = "";
      setFile(null);
      return;
    }

    setFile(selected);
  };

  const { mutateAsync, isLoading } = useMutation(
    (selected) => uploadFacilityServices(selected),
    {
      onSuccess: (res) => {
        if (res?.status === 200) {
          setResult(res.data.data);

          if (res.data.data.created > 0) {
            message.success(res.data.message);
            onSuccess?.();
          } else {
            message.warning("no services were created — see the details below");
          }
          return;
        }

        throw res;
      },
      onError: (error) => {
        const err = error?.response?.data?.message;
        if (_.isArray(err)) err.forEach((e) => message.error(e.message));
        else message.error(err || "upload failed");
      },
      retry: false,
    }
  );

  const handleUpload = async () => {
    if (!file) {
      message.error("select a file to upload");
      return;
    }
    await mutateAsync(file);
  };

  return (
    <div>
      <Alert color="teal" variant="light" className="mb-3">
        Upload services in bulk from a spreadsheet. Download the template, fill it
        in, then upload it here. Categories that do not exist yet are created
        automatically, and services whose name or code already exists are skipped
        rather than overwritten.
      </Alert>

      <button
        type="button"
        onClick={handleDownloadTemplate}
        disabled={isDownloading}
        className={`button app-btn btn-text mb-3 ${isDownloading && " is-loading"}`}>
        <span className="bi bi-download me-2"></span>
        Download template
      </button>

      <div className="field">
        <label className="label" htmlFor="services-file">
          Excel file
        </label>
        <input
          id="services-file"
          className="input"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          disabled={isLoading}
        />
        <p className="help">accepted formats: .xlsx, .xls — maximum 10MB</p>
      </div>

      <button
        type="button"
        onClick={handleUpload}
        disabled={!file || isLoading}
        className={`button h6 btn-prim mt-3 ${isLoading && " is-loading"}`}
        style={{ minWidth: "8rem" }}>
        <span className="bi bi-upload me-2"></span>
        Upload
      </button>

      {result && (
        <div className="mt-4">
          <Alert
            color={result.created > 0 ? "teal" : "orange"}
            variant="light"
            title="Upload summary">
            <p className="mb-1">
              {result.created} of {result.totalRows} row(s) created,{" "}
              {result.skipped?.length || 0} skipped,{" "}
              {result.errors?.length || 0} failed.
            </p>
            {result.createdCategories?.length > 0 && (
              <p className="mb-0 small">
                new categories created: {result.createdCategories.join(", ")}
              </p>
            )}
          </Alert>

          <RowIssues title="Skipped" rows={result.skipped} tone="warning" />
          <RowIssues title="Failed" rows={result.errors} tone="danger" />
        </div>
      )}
    </div>
  );
}
